import { LanguageVariantsSection } from "@/components/language-variants-section";
import { BuilderSection } from "@/components/studio/builder-section";
import { IntentTaggingSection } from "@/components/studio/intent-tagging-section";
import { ReadOnlyBanner } from "@/components/studio/read-only-banner";
import { UploadDropzone } from "@/components/studio/upload-dropzone";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useUpload } from "@/hooks/use-upload";
import { ContentAlbumService } from "@/lib/api/album.service";
import { audioService, type AudioListItem } from "@/lib/api/audio.service";
import { categoryService } from "@/lib/api/categories.service";
import { ContentTagService } from "@/lib/api/content.tag.service";
import { videoService, type VideoListItem } from "@/lib/api/video.service";
import { useAuth } from "@/lib/auth";
import { queryClient } from "@/lib/queryClient";
import type {
  AlbumItem,
  AlbumItemContentType,
  AlbumType,
  Category,
  StudioAlbum,
  TagGroup,
  TagOption,
} from "@/lib/schema";
import { SUPPORTED_LANGUAGES, getLanguageLabel } from "@/lib/studio-constants";
import {
  THUMBNAIL_ACCEPT_ATTR,
  THUMBNAIL_HINT,
  validateThumbnailFile,
} from "@/lib/thumbnail-validation";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Album,
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Globe,
  ListMusic,
  Loader2,
  Minus,
  Music,
  Save,
  Search,
  Trash2,
  Video,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "wouter";

interface AlbumVariantDraft {
  id?: string;
  languageCode: string;
  isDefault: boolean;
  localizedTitle: string;
  localizedDescription: string;
}

interface AlbumLanguageVariant {
  id: string;
  albumId: string;
  languageCode: string;
  isDefault: boolean;
  localizedTitle: string | null;
  localizedDescription: string | null;
}

type CompatibilityLevel = "perfect" | "compatible" | "neutral" | "conflicting";

export default function AlbumEditPage({ id }: { id: string }) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const { uploadFile, isUploading } = useUpload({
    onSuccess: (response) => {
      setFormCoverImage(response.objectPath);
    },
  });

  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formAlbumType, setFormAlbumType] = useState("");
  const [formCategoryId, setFormCategoryId] = useState("");
  const [formCoverImage, setFormCoverImage] = useState("");
  const [formTagOptionIds, setFormTagOptionIds] = useState<string[]>([]);
  const [initialized, setInitialized] = useState(false);

  const [variants, setVariants] = useState<AlbumVariantDraft[]>([]);
  const [activeVariantIndex, setActiveVariantIndex] = useState(0);
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState<number | null>(
    null,
  );

  const [deletedVariantIds, setDeletedVariantIds] = useState<string[]>([]);
  const [contentSearchQuery, setContentSearchQuery] = useState("");
  const [compatWarning, setCompatWarning] = useState<{
    contentId: string;
    contentType: AlbumItemContentType;
    title: string;
  } | null>(null);
  const [pendingAlbumType, setPendingAlbumType] = useState<AlbumType | null>(
    null,
  );

  const { data: album, isLoading } = useQuery<StudioAlbum>({
    queryKey: ["content-albums", id],
    queryFn: () => ContentAlbumService.findOne(id),
    enabled: !!id,
  });

  const isOwner = !!user && !!album?.createdBy && album.createdBy === user.id;
  // Editable only by admin/super_admin or the owning coach; every other role
  // (coach_manager, content_manager, influencer, non-owner coach) is read-only.
  const isAdmin = !!user?.roles?.some((r) => ["admin", "super_admin"].includes(r));
  const isCoach = !!user?.roles?.includes("coach");
  const isReadOnly = !(isAdmin || (isCoach && isOwner));

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["/content-category"],
    queryFn: () => categoryService.findAll(),
  });
  const { data: tagGroups = [] } = useQuery<TagGroup[]>({
    queryKey: ["/content/tag-groups"],
    queryFn: () => ContentTagService.getTagGroups(),
  });
  const { data: tagOptions = [] } = useQuery<TagOption[]>({
    queryKey: ["/content/tag-options"],
    queryFn: () => ContentTagService.getTagOptions(),
  });
  const { data: fetchedVariants } = useQuery<AlbumLanguageVariant[]>({
    queryKey: ["content-albums", id, "variants"],
    queryFn: () => ContentAlbumService.getVariants(id),
    enabled: !!id,
  });

  const { data: albumItems = [] } = useQuery<AlbumItem[]>({
    queryKey: ["content-albums", id, "items"],
    queryFn: () => ContentAlbumService.getItems(id),
    enabled: !!id,
  });

  // shortcut: compatibility ranking needs every published audio client-side; capped at 1000, move ranking server-side past that.
  const { data: audioContents = [] } = useQuery({
    queryKey: ["/audio", { status: "published", limit: 1000 }],
    queryFn: () =>
      audioService
        .list({ status: "published", limit: 1000 })
        .then((r) => r.items),
  });
  const { data: videoContents = [] } = useQuery({
    queryKey: ["/video"],
    queryFn: () => videoService.list(),
  });
  const getGroupOptions = useCallback(
    (groupId: string) =>
      tagOptions.filter((o) => o.groupId === groupId && o.status === "active"),
    [tagOptions],
  );

  const toggleTagOption = (tagId: string) => {
    setFormTagOptionIds((prev) =>
      prev.includes(tagId)
        ? prev.filter((tid) => tid !== tagId)
        : [...prev, tagId],
    );
  };

  useEffect(() => {
    if (album && !initialized) {
      setFormName(album.name);
      setFormDescription(album.description || "");
      setFormAlbumType(album.albumType || "audio");
      setFormCategoryId(album.categoryId || "");
      setFormCoverImage(album.coverImage || "");
      setFormTagOptionIds(album.tagOptionIds || []);
      setInitialized(true);
    }
  }, [album, initialized]);

  useEffect(() => {
    if (
      fetchedVariants &&
      fetchedVariants.length > 0 &&
      variants.length === 0
    ) {
      setVariants(
        fetchedVariants.map((v) => ({
          id: v.id,
          languageCode: v.languageCode,
          isDefault: v.isDefault,
          localizedTitle: v.localizedTitle || "",
          localizedDescription: v.localizedDescription || "",
        })),
      );
      setActiveVariantIndex(0);
    }
  }, [fetchedVariants]);

  const activeVariant = variants[activeVariantIndex];
  const availableLanguages = SUPPORTED_LANGUAGES.filter(
    (lang) => !variants.some((v) => v.languageCode === lang.code),
  );

  function addVariant(langCode: string) {
    setVariants((prev) => [
      ...prev,
      {
        languageCode: langCode,
        isDefault: false,
        localizedTitle: "",
        localizedDescription: "",
      },
    ]);
    setActiveVariantIndex(variants.length);
  }

  function setAsDefault(index: number) {
    setVariants((prev) =>
      prev.map((v, i) => ({ ...v, isDefault: i === index })),
    );
  }

  function confirmRemoveVariant() {
    if (pendingRemoveIndex === null) return;
    const removing = variants[pendingRemoveIndex];
    if (removing?.id) {
      setDeletedVariantIds((prev) => [...prev, removing.id!]);
    }
    setVariants((prev) => {
      const next = prev.filter((_, i) => i !== pendingRemoveIndex);
      if (removing?.isDefault && next.length > 0) {
        next[0] = { ...next[0], isDefault: true };
      }
      return next;
    });
    setActiveVariantIndex(0);
    setPendingRemoveIndex(null);
  }

  function updateVariantField(field: keyof AlbumVariantDraft, value: string) {
    setVariants((prev) =>
      prev.map((v, i) =>
        i === activeVariantIndex ? { ...v, [field]: value } : v,
      ),
    );
  }

  async function handleCoverUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const validation = await validateThumbnailFile(file);
    if (!validation.valid) {
      toast({
        title: "Invalid image",
        description: validation.error,
        variant: "destructive",
      });
      e.target.value = "";
      return;
    }
    await uploadFile(file);
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      await ContentAlbumService.update(id, {
        name: formName,
        description: formDescription || null,
        albumType: formAlbumType as AlbumType,
        categoryId:
          formCategoryId && formCategoryId !== "none" ? formCategoryId : null,
        tagOptionIds: formTagOptionIds,
        coverImage: formCoverImage || null,
      });

      for (const delId of deletedVariantIds) {
        await ContentAlbumService.deleteVariant(delId);
      }

      for (const v of variants) {
        const localizedTitle = v.isDefault
          ? formName
          : v.localizedTitle || null;
        const localizedDescription = v.isDefault
          ? formDescription || null
          : v.localizedDescription || null;
        if (v.id) {
          await ContentAlbumService.updateVariant(v.id, {
            localizedTitle,
            localizedDescription,
            isDefault: v.isDefault,
          });
        } else {
          await ContentAlbumService.createVariant(id, {
            languageCode: v.languageCode,
            isDefault: v.isDefault,
            localizedTitle,
            localizedDescription,
          });
        }
      }
    },
    onSuccess: () => {
      setDeletedVariantIds([]);
      queryClient.invalidateQueries({ queryKey: ["content-albums"] });
      queryClient.invalidateQueries({ queryKey: ["content-albums", id] });
      queryClient.invalidateQueries({
        queryKey: ["content-albums", id, "variants"],
      });
      toast({ title: "Album saved" });
    },
    onError: () => {
      toast({ title: "Failed to save album", variant: "destructive" });
    },
  });

  interface EnrichedContent {
    id: string;
    title: string;
    type: AlbumItemContentType;
    status: string;
    categoryId?: string | null;
    tagOptionIds?: string[];
    updatedAt: string;
  }

  const allContent = useMemo<EnrichedContent[]>(() => {
    const items: EnrichedContent[] = [];
    (audioContents as AudioListItem[]).forEach((a) => {
      items.push({
        id: a.id,
        title: a.primaryTitle || "Untitled",
        type: "audio",
        status: a.status,
        categoryId: a.categoryId,
        tagOptionIds: a.tagOptionIds || [],
        updatedAt: a.updatedAt,
      });
    });
    (videoContents as VideoListItem[]).forEach((v) => {
      items.push({
        id: v.id,
        title: v.primaryTitle || "Untitled",
        type: "video",
        status: v.status,
        categoryId: v.categoryId,
        tagOptionIds: v.tagOptionIds || [],
        updatedAt: v.updatedAt,
      });
    });
    return items;
  }, [audioContents, videoContents]);

  function expandTagsWithRelations(tagIds: string[]): Set<string> {
    const expanded = new Set(tagIds);
    const tagMap = new Map(tagOptions.map((t) => [t.id, t]));
    for (const tagId of tagIds) {
      const tag = tagMap.get(tagId);
      if (tag?.parentTagOptionIds) {
        for (const pid of tag.parentTagOptionIds) {
          if (tagMap.has(pid)) expanded.add(pid);
        }
      }
    }
    for (const opt of tagOptions) {
      if (opt.parentTagOptionIds?.some((pid) => tagIds.includes(pid))) {
        expanded.add(opt.id);
      }
    }
    return expanded;
  }

  function computeCompatibility(
    contentCategoryId: string | null | undefined,
    contentTagIds: string[],
    albumCategoryId: string | null,
    albumTagIds: string[] = [],
  ): CompatibilityLevel {
    if (!albumCategoryId && albumTagIds.length === 0) return "neutral";
    const categoryMatch =
      !albumCategoryId || contentCategoryId === albumCategoryId;
    if (albumTagIds.length === 0)
      return categoryMatch ? "compatible" : "conflicting";
    const expandedContent =
      tagOptions.length > 0
        ? expandTagsWithRelations(contentTagIds)
        : new Set(contentTagIds);
    const expandedAlbum =
      tagOptions.length > 0
        ? expandTagsWithRelations(albumTagIds)
        : new Set(albumTagIds);
    const overlap = Array.from(expandedAlbum).filter((t) =>
      expandedContent.has(t),
    ).length;
    if (categoryMatch && overlap > 0) return "perfect";
    if (categoryMatch && contentTagIds.length === 0) return "compatible";
    if (categoryMatch) return "compatible";
    if (overlap > 0) return "compatible";
    if (
      contentCategoryId &&
      albumCategoryId &&
      contentCategoryId !== albumCategoryId
    )
      return "conflicting";
    return "neutral";
  }

  const compatConfig: Record<
    CompatibilityLevel,
    { label: string; color: string; icon: React.ElementType }
  > = {
    perfect: {
      label: "Perfect match",
      color: "text-green-600 dark:text-green-400",
      icon: CheckCircle2,
    },
    compatible: {
      label: "Compatible",
      color: "text-blue-600 dark:text-blue-400",
      icon: CheckCircle2,
    },
    neutral: { label: "Neutral", color: "text-muted-foreground", icon: Minus },
    conflicting: {
      label: "Mismatch",
      color: "text-amber-600 dark:text-amber-400",
      icon: AlertTriangle,
    },
  };
  const compatOrder: Record<CompatibilityLevel, number> = {
    perfect: 0,
    compatible: 1,
    neutral: 2,
    conflicting: 3,
  };

  function getContentTypeIcon(type: string) {
    switch (type) {
      case "audio":
        return <Music className="h-3.5 w-3.5 text-blue-500" />;
      case "video":
        return <Video className="h-3.5 w-3.5 text-purple-500" />;
      default:
        return null;
    }
  }

  const addItemMutation = useMutation({
    mutationFn: async ({
      contentId,
      contentType,
    }: {
      contentId: string;
      contentType: AlbumItemContentType;
    }) => {
      return ContentAlbumService.addItem(id, { contentId, contentType });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["content-albums", id, "items"],
      });
      queryClient.invalidateQueries({ queryKey: ["content-albums"] });
      toast({ title: "Content added to album" });
    },
    onError: () => {
      toast({ title: "Failed to add content", variant: "destructive" });
    },
  });

  const removeItemMutation = useMutation({
    mutationFn: async (itemId: string) => {
      await ContentAlbumService.removeItem(id, itemId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["content-albums", id, "items"],
      });
      queryClient.invalidateQueries({ queryKey: ["content-albums"] });
      toast({ title: "Content removed" });
    },
    onError: () => {
      toast({ title: "Failed to remove content", variant: "destructive" });
    },
  });

  function handleAddContent(
    contentId: string,
    contentType: AlbumItemContentType,
    title: string,
  ) {
    const compat = computeCompatibility(
      allContent.find((c) => c.id === contentId)?.categoryId,
      allContent.find((c) => c.id === contentId)?.tagOptionIds || [],
      formCategoryId && formCategoryId !== "none" ? formCategoryId : null,
      formTagOptionIds,
    );
    if (compat === "conflicting") {
      setCompatWarning({ contentId, contentType, title });
      return;
    }
    addItemMutation.mutate({ contentId, contentType });
  }

  const isSaving = saveMutation.isPending;
  const hasEmptyNonPrimaryVariant = variants.some(
    (v) => !v.isDefault && !v.localizedTitle.trim(),
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!album) {
    return (
      <div className="p-6 text-center">
        <p className="text-muted-foreground">Album not found</p>
        <Link href="/studio/albums">
          <Button variant="outline" className="mt-4">
            Back to Albums
          </Button>
        </Link>
      </div>
    );
  }

  const alreadyAdded = new Set(
    albumItems.map(
      (item) => `${item.contentType}:${item.entityId ?? item.contentId}`,
    ),
  );

  const available = allContent
    .filter((c) => {
      if (formAlbumType && c.type !== formAlbumType) return false;
      if (c.status !== "published") return false;
      if (alreadyAdded.has(`${c.type}:${c.id}`)) return false;
      if (
        contentSearchQuery &&
        !c.title.toLowerCase().includes(contentSearchQuery.toLowerCase())
      )
        return false;
      return true;
    })
    .map((c) => ({
      ...c,
      compatibility: computeCompatibility(
        c.categoryId,
        c.tagOptionIds || [],
        formCategoryId && formCategoryId !== "none" ? formCategoryId : null,
        formTagOptionIds,
      ),
    }))
    .sort((a, b) => {
      const compatDiff =
        compatOrder[a.compatibility] - compatOrder[b.compatibility];
      if (compatDiff !== 0) return compatDiff;
      return (b.updatedAt || "").localeCompare(a.updatedAt || "");
    })
    .slice(0, 50);

  return (
    <div
      className="p-6 space-y-6 max-w-4xl mx-auto"
      data-testid="album-edit-page"
    >
      {isReadOnly && <ReadOnlyBanner ownedByOther={!isOwner} />}
      <div className="flex items-center gap-4 flex-wrap">
        <Link href="/studio/albums">
          <Button variant="ghost" size="icon" data-testid="button-back">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div className="flex-1 min-w-0">
          <h1
            className="font-serif text-2xl font-semibold text-foreground"
            data-testid="text-page-title"
          >
            {formName || "Edit Album"}
          </h1>
          <div className="flex items-center gap-2 mt-0.5">
            <Badge variant="default" className="text-xs">
              {album.status === "active" ? "Active" : "Archived"}
            </Badge>
            {formAlbumType && (
              <Badge variant="outline" className="text-xs capitalize">
                {getContentTypeIcon(formAlbumType)}
                <span className="ml-1">{formAlbumType}</span>
              </Badge>
            )}
          </div>
        </div>
        {!isReadOnly && (
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={
              !formName.trim() ||
              !formAlbumType ||
              hasEmptyNonPrimaryVariant ||
              isSaving
            }
            data-testid="button-save"
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Changes
          </Button>
        )}
      </div>

      <BuilderSection
        icon={Album}
        title="Basic Info"
        description="Name, type, description, and cover image"
        defaultOpen
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="album-name">Name *</Label>
            <Input
              id="album-name"
              placeholder="e.g. Morning Calm, Evening Wind Down"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              data-testid="input-album-name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="album-type">Album Type *</Label>
            <Select
              value={formAlbumType}
              onValueChange={(next) => {
                if (
                  next !== formAlbumType &&
                  albumItems.length > 0 &&
                  (next === "audio" || next === "video")
                ) {
                  setPendingAlbumType(next);
                  return;
                }
                setFormAlbumType(next);
              }}
            >
              <SelectTrigger id="album-type" data-testid="select-album-type">
                <SelectValue placeholder="Choose album type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="audio">
                  <span className="flex items-center gap-2">
                    <Music className="h-4 w-4 text-blue-500" /> Audio
                  </span>
                </SelectItem>
                <SelectItem value="video">
                  <span className="flex items-center gap-2">
                    <Video className="h-4 w-4 text-purple-500" /> Video
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="album-description">Description</Label>
            <Textarea
              id="album-description"
              placeholder="A short description of this album..."
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              className="resize-none"
              rows={3}
              data-testid="input-album-description"
            />
          </div>

          <div className="space-y-2">
            <Label>Cover Image</Label>
            {formCoverImage ? (
              <div className="relative inline-block">
                <img
                  src={formCoverImage}
                  alt="Cover preview"
                  className="w-40 h-24 rounded-md object-cover"
                />
                <Button
                  variant="secondary"
                  size="icon"
                  className="absolute -top-2 -right-2 h-6 w-6 rounded-full"
                  onClick={() => setFormCoverImage("")}
                  data-testid="button-remove-cover"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <UploadDropzone
                type="image"
                label="Upload Cover"
                hint={THUMBNAIL_HINT}
                accept={THUMBNAIL_ACCEPT_ATTR}
                onChange={handleCoverUpload}
                isUploading={isUploading}
                testId="input-cover-upload"
              />
            )}
          </div>
        </div>
      </BuilderSection>

      <IntentTaggingSection
        categories={categories}
        categoryId={formCategoryId}
        onCategoryChange={setFormCategoryId}
        tagGroups={tagGroups}
        getGroupOptions={getGroupOptions}
        selectedTagOptionIds={formTagOptionIds}
        onToggleTagOption={toggleTagOption}
      />

      <BuilderSection
        icon={ListMusic}
        title="Album Content"
        description={`${albumItems.length} item${albumItems.length !== 1 ? "s" : ""} in this album`}
        badge={`${albumItems.length}`}
        defaultOpen
      >
        <div className="space-y-4">
          {albumItems.length > 0 && (
            <div className="space-y-1">
              <Label className="text-sm font-medium">Current Content</Label>
              <div className="border rounded-md divide-y">
                {albumItems.map((item, index) => (
                  <div
                    key={item.id}
                    className="flex items-center gap-3 px-3 py-2"
                    data-testid={`album-item-${item.id}`}
                  >
                    <span className="text-xs text-muted-foreground w-5 text-center">
                      {index + 1}
                    </span>
                    {getContentTypeIcon(item.contentType)}
                    <span className="text-sm flex-1 truncate">
                      {item.contentTitle ?? item.entityId ?? item.contentId}
                    </span>
                    <Badge variant="outline" className="text-[10px] capitalize">
                      {item.contentType}
                    </Badge>
                    {!isReadOnly && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={() => removeItemMutation.mutate(item.id)}
                        data-testid={`button-remove-item-${item.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {!isReadOnly && (
          <div className="space-y-2">
            <Label className="text-sm font-medium">Add Content</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder={`Search ${formAlbumType || "content"} to add...`}
                value={contentSearchQuery}
                onChange={(e) => setContentSearchQuery(e.target.value)}
                data-testid="input-content-search"
              />
            </div>
            <div className="border rounded-md max-h-[300px] overflow-y-auto divide-y">
              {available.length === 0 ? (
                <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                  No matching content available
                </div>
              ) : (
                available.map((c) => {
                  const cfg = compatConfig[c.compatibility];
                  const Icon = cfg.icon;
                  return (
                    <div
                      key={`${c.type}-${c.id}`}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-muted/50"
                      data-testid={`available-content-${c.id}`}
                    >
                      {getContentTypeIcon(c.type)}
                      <span className="text-sm flex-1 truncate">{c.title}</span>
                      <span
                        className={`flex items-center gap-1 text-[10px] ${cfg.color}`}
                      >
                        <Icon className="h-3 w-3" />
                        {cfg.label}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => handleAddContent(c.id, c.type, c.title)}
                        data-testid={`button-add-content-${c.id}`}
                      >
                        Add
                      </Button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
          )}
        </div>
      </BuilderSection>

      <BuilderSection
        icon={Globe}
        title="Language Variants"
        description="Translations for album title and description"
        badge={`${variants.length} language${variants.length !== 1 ? "s" : ""}`}
        defaultOpen={false}
      >
        <LanguageVariantsSection
          variants={variants}
          activeVariantIndex={activeVariantIndex}
          onActiveVariantChange={setActiveVariantIndex}
          availableLanguages={availableLanguages}
          onAddVariant={addVariant}
          onSetDefault={setAsDefault}
          pendingRemoveIndex={pendingRemoveIndex}
          onRequestRemove={setPendingRemoveIndex}
          onConfirmRemove={confirmRemoveVariant}
          onCancelRemove={() => setPendingRemoveIndex(null)}
        >
          {activeVariant && (
            <div className="space-y-3 pt-3">
              {activeVariant.isDefault ? (
                <div className="space-y-3">
                  <p className="text-xs text-muted-foreground">
                    Primary language ({getLanguageLabel(activeVariant.languageCode)}) uses the Name and Description from Basic Info.
                  </p>
                  <div className="space-y-2">
                    <Label>Localized Title</Label>
                    <Input
                      value={formName}
                      readOnly
                      disabled
                      placeholder={`Title in ${getLanguageLabel(activeVariant.languageCode)}`}
                      data-testid={`input-localized-title-${activeVariant.languageCode}`}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Localized Description</Label>
                    <Textarea
                      value={formDescription}
                      readOnly
                      disabled
                      placeholder={`Description in ${getLanguageLabel(activeVariant.languageCode)}`}
                      rows={2}
                      data-testid={`input-localized-description-${activeVariant.languageCode}`}
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label>
                      Localized Title
                      <span className="text-destructive ml-1">*</span>
                    </Label>
                    <Input
                      value={activeVariant.localizedTitle}
                      onChange={(e) =>
                        updateVariantField("localizedTitle", e.target.value)
                      }
                      placeholder={`Title in ${getLanguageLabel(activeVariant.languageCode)}`}
                      data-testid={`input-localized-title-${activeVariant.languageCode}`}
                    />
                    {!activeVariant.localizedTitle.trim() && (
                      <p className="text-xs text-destructive">
                        Title required for non-default language.
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Localized Description</Label>
                    <Textarea
                      value={activeVariant.localizedDescription}
                      onChange={(e) =>
                        updateVariantField("localizedDescription", e.target.value)
                      }
                      placeholder={`Description in ${getLanguageLabel(activeVariant.languageCode)}`}
                      rows={2}
                      data-testid={`input-localized-description-${activeVariant.languageCode}`}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </LanguageVariantsSection>
      </BuilderSection>

      <AlertDialog
        open={!!compatWarning}
        onOpenChange={(open) => {
          if (!open) setCompatWarning(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Content Mismatch</AlertDialogTitle>
            <AlertDialogDescription>
              "{compatWarning?.title}" doesn't match this album's category or
              tags. Are you sure you want to add it?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (compatWarning) {
                  addItemMutation.mutate({
                    contentId: compatWarning.contentId,
                    contentType: compatWarning.contentType,
                  });
                  setCompatWarning(null);
                }
              }}
            >
              Add Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!pendingAlbumType}
        onOpenChange={(open) => {
          if (!open) setPendingAlbumType(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change Album Type?</AlertDialogTitle>
            <AlertDialogDescription>
              This album has {albumItems.length}{" "}
              {albumItems.length === 1 ? "item" : "items"} of type "
              {formAlbumType}". Switching to "{pendingAlbumType}" will leave
              existing items in the album but they won't match the new type.
              You may want to remove them after switching. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingAlbumType) {
                  setFormAlbumType(pendingAlbumType);
                  setPendingAlbumType(null);
                }
              }}
            >
              Change Type
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
